import { ReevaluateOptIn, ReevaluateOptIns } from '../../api/reevaluation';
import { environmentKey } from './issueFormat';

/** One environment as the list on the Herbeoordeling tab shows it. */
export interface AfterDeployRow {
    key: string;
    customer: string;
    environment: string;
    /** Whether the rollout watch starts a dry run after a deploy. */
    on: boolean;
    /** Whether the environment chose itself; otherwise Rollout:Reevaluate decides. */
    own: boolean;
    changedBy?: string | null;
    changedAt?: string | null;
}

const time = (optIn?: ReevaluateOptIn | null) => (optIn?.changedAt ? Date.parse(optIn.changedAt) : 0);

/**
 * The newer of two readings of one environment. A read that was under way while someone saved
 * comes back with the older choice; it must not undo what is on screen.
 */
export const latestOf = (
    shown: ReevaluateOptIn | null | undefined,
    read: ReevaluateOptIn | null | undefined
): ReevaluateOptIn | null => {
    if (!shown) {
        return read ?? null;
    }

    if (!read) {
        return shown;
    }

    return time(read) >= time(shown) ? read : shown;
};

/** The list after a save or a read of one environment, keeping every other environment as it was. */
export const withChoice = (optIns: ReevaluateOptIns, choice: ReevaluateOptIn): ReevaluateOptIns => {
    const key = environmentKey(choice.customer, choice.environment);
    const current = optIns.environments.find((o) => environmentKey(o.customer, o.environment) === key);
    const kept = latestOf(current, choice) ?? choice;

    return {
        ...optIns,
        environments: [
            ...optIns.environments.filter((o) => environmentKey(o.customer, o.environment) !== key),
            kept,
        ],
    };
};

/**
 * Every environment with whether a deploy is followed by a dry run: its own choice, or the app
 * setting. Environments the page does not know but that chose are listed too.
 */
export const afterDeployRows = (
    environments: { customer: string; environment: string }[],
    optIns: ReevaluateOptIns | null
): AfterDeployRow[] => {
    const configured = optIns?.configured === true;
    const chosen = new Map(
        (optIns?.environments ?? []).map((o) => [environmentKey(o.customer, o.environment), o] as const)
    );
    const rows = new Map<string, AfterDeployRow>();

    for (const e of [...environments, ...(optIns?.environments ?? [])]) {
        const key = environmentKey(e.customer, e.environment);

        if (rows.has(key)) {
            continue;
        }

        const own = chosen.get(key);
        const hasOwn = typeof own?.enabled === 'boolean';

        rows.set(key, {
            key,
            customer: e.customer,
            environment: e.environment,
            on: hasOwn ? own?.enabled === true : configured,
            own: hasOwn,
            changedBy: own?.changedBy,
            changedAt: own?.changedAt,
        });
    }

    return [...rows.values()].sort((a, b) => a.key.localeCompare(b.key));
};

/** "Na deploy: aan" or "Na deploy: uit". */
export const afterDeployLabel = (on: boolean) => `Na deploy: ${on ? 'aan' : 'uit'}`;
